import { normalizeCurrency } from '@renderer/utils/formatCurrency'

/**
 * Which languages a kiosk offers, derived from the currency stamped on the
 * loaded project. Kept free of React and of any store import so it can be
 * unit-tested and reused from plain modules; the hooks in
 * `@renderer/hooks/useLanguageGating` wrap it for components.
 */

export type BoothLanguage = 'en' | 'id' | 'es'

/**
 * Currencies of Spanish-speaking markets. Deliberately excludes BRL
 * (Portuguese) and USD — USD is the fallback currency for a long tail of
 * unrelated countries, so it carries no language signal.
 */
const SPANISH_CURRENCIES = new Set([
  'MXN',
  'ARS',
  'CLP',
  'COP',
  'PEN',
  'UYU',
  'BOB',
  'PYG',
  'VES',
  'GTQ',
  'CRC',
  'DOP',
  'PAB',
  'HNL',
  'NIO'
])

/**
 * "IDR-context" means the Indonesian picker is meaningful: the project is
 * stamped IDR, or no project is loaded at all (operator chrome / login /
 * dashboard / fresh kiosk install pre-config). Legacy projects without a
 * `resolvedCurrency` are treated as IDR — that's the kiosk's historical
 * default and what `normalizeCurrency` falls back to anyway.
 */
export function isIdrContext(currency: string | null | undefined): boolean {
  if (!currency) return true // No project / pre-config — treat as IDR
  return normalizeCurrency(currency) === 'IDR'
}

/**
 * The languages a project offers, the project's own language first.
 */
export function languagesForCurrency(currency: string | null | undefined): BoothLanguage[] {
  if (isIdrContext(currency)) return ['id', 'en']
  if (SPANISH_CURRENCIES.has(normalizeCurrency(currency))) return ['es', 'en']
  return ['en']
}
